import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, Lock, User, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const SYMBOL_LOGO = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/zntnld86_Lifely%20Symbol%20AI.png";
const TEXT_LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/yxqxnzye_ReGenflo%20logo%202%20contrast.png";

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({ name: '', password: '', confirmPassword: '' });

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid or missing invitation link');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/invites/verify?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setInviteData(response.data.invite);
          setFormData(prev => ({ ...prev, name: response.data.invite.name }));
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or used invitation link');
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/users/accept-invite`, {
        token,
        password: formData.password,
        name: formData.name
      });
      setSuccess(true);
      setTimeout(() => navigate('/admin'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center">
        <div className="text-center">
          <img src={SYMBOL_LOGO} alt="Lifely" className="w-16 h-16 mx-auto mb-6 animate-pulse" />
          <p className="text-white/60">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-['Outfit'] font-bold text-white mb-4" data-testid="invite-invalid-title">Invalid Invitation</h1>
          <p className="text-white/60 mb-8">{error || 'This invitation link is invalid or has already been used.'}</p>
          <Link to="/login">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
              Go to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-[#4E7A6B]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#4E7A6B]" />
          </div>
          <h1 className="text-2xl font-['Outfit'] font-bold text-white mb-4" data-testid="invite-success-title">Welcome to the Team</h1>
          <p className="text-white/60 mb-2">Your account has been activated.</p>
          <p className="text-white/40 text-sm">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img src={SYMBOL_LOGO} alt="Lifely" className="h-12 w-auto" />
          <img src={TEXT_LOGO_LIGHT} alt="ReGenflō" className="h-8 w-auto" />
        </div>

        <div className="bg-[#0D1320] rounded-xl border border-white/10 p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-['Outfit'] font-bold text-white mb-2" data-testid="accept-invite-title">Join the Care Team</h1>
            <p className="text-white/50 text-sm">Set up your password to get started</p>
          </div>

          <div className="p-4 rounded-lg bg-[#080C14] border border-white/5 mb-6 space-y-2">
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Email</p>
              <p className="text-white font-medium">{inviteData?.email}</p>
            </div>
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Role</p>
              <Badge className="bg-[#C89B7B]/20 text-[#C89B7B] border-[#C89B7B]/30 capitalize">
                {inviteData?.role?.replace('_', ' ')}
              </Badge>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400" data-testid="accept-invite-error">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/80">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="pl-10 bg-[#080C14] border-white/10 text-white placeholder:text-white/30 focus:border-[#C89B7B]"
                  required
                  data-testid="accept-invite-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimum 8 characters"
                  className="pl-10 bg-[#080C14] border-white/10 text-white placeholder:text-white/30 focus:border-[#C89B7B]"
                  required
                  data-testid="accept-invite-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Re-enter your password"
                  className="pl-10 bg-[#080C14] border-white/10 text-white placeholder:text-white/30 focus:border-[#C89B7B]"
                  required
                  data-testid="accept-invite-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#C89B7B] hover:bg-[#B88A6A] text-[#080C14] py-5"
              disabled={submitting}
              data-testid="accept-invite-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Activating...</>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
